import {
  Component,
  OnInit,
  signal,
  effect,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  viewChild,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { PermissionService } from '@app/core/services/permission.service';
import {
  NewUserFeedItemGQL,
  PageInfo,
  PermissionEnum,
  UpdateUserFeedItemAttributes,
  UpdateUserFeedItemGQL,
  UpdateUserFeedItemsGQL,
  UserFeedItem,
  UserFeedItemEdge,
  UserFeedItemsConnectionGQL,
  UserFeedUnseenCountGQL,
} from '@app/generated/graphql';
import { Popover } from 'primeng/popover';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { Ag1LoaderComponent } from '../../../../core/components/ag1-loader/ag1-loader.component';
import { TimeAgoPipe } from '../../../../core/pipes/time-ago.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Bind,
    Button,
    Tooltip,
    AngularSvgIconModule,
    Popover,
    NgTemplateOutlet,
    Ag1LoaderComponent,
    AsyncPipe,
    TimeAgoPipe,
  ],
})
export class NotificationsComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private permissionService = inject(PermissionService);
  private updateUserFeedItemsGQL = inject(UpdateUserFeedItemsGQL);
  private updateUserFeedItemGQL = inject(UpdateUserFeedItemGQL);
  private newUserFeedItemGQL = inject(NewUserFeedItemGQL);
  private userFeedItemsConnectionGQL = inject(UserFeedItemsConnectionGQL);
  private userFeedUnseenCountGql = inject(UserFeedUnseenCountGQL);
  private router = inject(Router);

  private readonly SCROLL_THRESHOLD = 10;

  readonly layout = input<'menu' | 'standalone'>('standalone');
  readonly userFeedUnseenItemCountChange = output<number>();

  userFeedEdges: UserFeedItemEdge[] = [];
  userFeedPageInfo: PageInfo | null = null;
  readonly userFeedUnseenItemCount = signal<number>(0);
  readonly showUserFeed = signal<boolean>(false);
  readonly isLoading = signal<boolean>(false);
  readonly isInitialLoading = signal<boolean>(true);
  readonly error = signal<string | null>(null);

  readonly userFeedPopover = viewChild.required<Popover>('userFeedPopover');

  constructor() {
    // Emit count changes whenever the signal changes
    effect(() => {
      this.userFeedUnseenItemCountChange.emit(this.userFeedUnseenItemCount());
    });
  }

  ngOnInit(): void {
    this.userFeedSubscriptions().catch((error) => {
      this.handleError(error, 'Failed to initialize notifications');
    });
  }

  async userFeedSubscriptions(): Promise<void> {
    try {
      const hasViewCommentsPermission = await this.permissionService.allow(
        PermissionEnum.ViewOrderViewComments,
      );
      const hasViewOrderPermission = await this.permissionService.allow(
        PermissionEnum.PagesViewOrder,
      );

      if (!hasViewCommentsPermission || !hasViewOrderPermission) {
        this.isInitialLoading.set(false);
        return;
      }

      this.isInitialLoading.set(true);
      this.error.set(null);

      // Fetch initial user feed items
      this.userFeedItemsConnectionGQL
        .fetch({ variables: { first: 5 } })
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          catchError((err) => {
            this.handleError(err, 'Failed to load notifications');
            return EMPTY;
          }),
          finalize(() => {
            this.isInitialLoading.set(false);
          }),
        )
        .subscribe((res) => {
          if (res?.data?.userFeedConnection) {
            this.userFeedEdges = [...res.data.userFeedConnection.edges];
            this.userFeedPageInfo = res.data.userFeedConnection.pageInfo;
          }
        });

      // Fetch initial unseen count
      this.userFeedUnseenCountGql
        .fetch()
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          catchError((err) => {
            this.handleError(err, 'Failed to load notification count');
            return EMPTY;
          }),
        )
        .subscribe((res) => {
          if (res?.data?.userFeedUnseenCount !== undefined) {
            this.userFeedUnseenItemCount.set(res.data.userFeedUnseenCount);
          }
        });

      // Subscribe to new user feed items
      this.newUserFeedItemGQL
        .subscribe()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          this.userFeedItemsConnectionGQL
            .fetch({ variables: { first: 1 } })
            .pipe(
              takeUntilDestroyed(this._destroyRef),
              catchError((err) => {
                this.handleError(err, 'Failed to fetch new notification');
                return EMPTY;
              }),
            )
            .subscribe((gqlResponse) => {
              if (gqlResponse?.data?.userFeedConnection?.edges) {
                const newEdges = gqlResponse.data.userFeedConnection.edges;
                // Prevent duplicates
                const existingIds = new Set(
                  this.userFeedEdges.map((edge) => edge.node.id),
                );
                const uniqueNewEdges = newEdges.filter(
                  (edge) => !existingIds.has(edge.node.id),
                );

                if (uniqueNewEdges.length > 0) {
                  this.userFeedEdges = [
                    ...uniqueNewEdges,
                    ...this.userFeedEdges,
                  ];
                  // Only increment count if popover is not visible
                  if (!this.showUserFeed()) {
                    this.userFeedUnseenItemCount.update((count) => count + 1);
                  }
                }
              }
            });
        });
    } catch (error) {
      this.handleError(
        error,
        'Failed to initialize notification subscriptions',
      );
      this.isInitialLoading.set(false);
      throw error; // Re-throw to be caught by ngOnInit's catch
    }
  }

  onUserFeedScroll(event: Event): void {
    const target: HTMLElement = event.target as HTMLElement;
    if (!target) {
      return;
    }

    const remainingScroll: number =
      target.scrollHeight - target.scrollTop - target.offsetHeight;

    if (remainingScroll < this.SCROLL_THRESHOLD) {
      this.fetchMoreUserFeedItems();
    }
  }

  markUserFeedItemAsRead(userFeedItem: UserFeedItem): void {
    if (!userFeedItem?.id) {
      return;
    }

    // Find the edge in our local state
    const edgeIndex: number = this.userFeedEdges.findIndex(
      (edge) => edge.node.id === userFeedItem.id,
    );
    const wasUnseen: boolean =
      edgeIndex >= 0 && !this.userFeedEdges[edgeIndex].node.seen;

    this.updateUserFeedItemGQL
      .mutate({
        variables: {
          attributes: {
            id: userFeedItem.id,
            read: true,
            seen: true,
          },
        },
      })
      .pipe(
        catchError((err) => {
          this.handleError(err, 'Failed to mark notification as read');
          return EMPTY;
        }),
        tap(() => {
          // Update local state by creating new objects (avoiding mutation)
          if (edgeIndex >= 0) {
            this.userFeedEdges = this.userFeedEdges.map((edge, index) => {
              if (index === edgeIndex) {
                return {
                  ...edge,
                  node: {
                    ...edge.node,
                    read: true,
                    seen: true,
                  },
                };
              }
              return edge;
            });

            if (wasUnseen) {
              this.userFeedUnseenItemCount.update((count) =>
                Math.max(0, count - 1),
              );
            }
          }
        }),
        finalize(() => {
          // Refetch count to sync with server
          this.refetchUnseenCount();
        }),
      )
      .subscribe();
  }

  navigateUserFeedItemRoute(userFeedItem: UserFeedItem): void {
    if (!userFeedItem?.notifiable) {
      return;
    }

    try {
      switch (userFeedItem.notifiable.__typename) {
        case 'Comment':
          if (
            userFeedItem.notifiable.commentable?.__typename === 'Order' &&
            userFeedItem.notifiable.commentable.id
          ) {
            this.router.navigate([
              '/account/order/',
              userFeedItem.notifiable.commentable.id,
            ]);
          } else {
            this.handleError(
              new Error(
                `Unable to navigate from user feed item for commentable ${userFeedItem.notifiable.commentable?.__typename}`,
              ),
              'Failed to navigate to notification',
            );
          }
          break;

        default:
          this.handleError(
            new Error(
              `Unable to navigate from user feed item for notifiable ${userFeedItem.notifiable.__typename}`,
            ),
            'Failed to navigate to notification',
          );
      }
    } catch (error) {
      this.handleError(error, 'Failed to navigate to notification');
    }
  }

  closeUserFeedPopover(): void {
    this.userFeedPopover().hide();
  }

  onUserFeedItemClick(userFeedItem: UserFeedItem): void {
    this.markUserFeedItemAsRead(userFeedItem);
    this.navigateUserFeedItemRoute(userFeedItem);
    this.closeUserFeedPopover();
  }

  fetchMoreUserFeedItems(): void {
    if (
      !this.userFeedPageInfo?.hasNextPage ||
      this.isLoading() ||
      this.userFeedEdges.length === 0
    ) {
      return;
    }

    const lastEdge: UserFeedItemEdge | undefined =
      this.userFeedEdges[this.userFeedEdges.length - 1];

    if (!lastEdge?.cursor) {
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    this.userFeedItemsConnectionGQL
      .fetch({
        variables: {
          first: 5,
          after: lastEdge.cursor,
        },
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((err) => {
          this.handleError(err, 'Failed to load more notifications');
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe((res) => {
        if (res?.data?.userFeedConnection) {
          this.userFeedEdges = [
            ...this.userFeedEdges,
            ...res.data.userFeedConnection.edges,
          ];
          this.userFeedPageInfo = res.data.userFeedConnection.pageInfo;
          this.markAllUserFeedItemsAsSeen();
        }
      });
  }

  onUserFeedPopoverShow(): void {
    this.showUserFeed.set(true);
  }

  onUserFeedPopoverHide(): void {
    this.showUserFeed.set(false);
    this.markAllUserFeedItemsAsSeen();
  }

  markAllUserFeedItemsAsSeen(): void {
    const unseenAttributes: UpdateUserFeedItemAttributes[] = this.userFeedEdges
      .filter((edge) => edge?.node && !edge.node.seen)
      .map((edge) => ({
        id: edge.node.id,
        seen: true,
      }));

    if (unseenAttributes.length > 0) {
      this.updateUserFeedItemsGQL
        .mutate({
          variables: {
            attributes: unseenAttributes,
          },
        })
        .pipe(
          catchError((err) => {
            this.handleError(err, 'Failed to mark notifications as seen');
            return EMPTY;
          }),
          finalize(() => {
            // Refetch count to sync with server
            this.refetchUnseenCount();
          }),
        )
        .subscribe(() => {
          // Update local state by creating new objects (avoiding mutation)
          this.userFeedEdges = this.userFeedEdges.map((edge) => {
            if (edge.node && !edge.node.seen) {
              return {
                ...edge,
                node: {
                  ...edge.node,
                  seen: true,
                },
              };
            }
            return edge;
          });
        });
    }
  }

  private refetchUnseenCount(): void {
    this.userFeedUnseenCountGql
      .fetch()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((err) => {
          this.handleError(err, 'Failed to refetch unseen count');
          return EMPTY;
        }),
      )
      .subscribe((res) => {
        if (res?.data?.userFeedUnseenCount !== undefined) {
          this.userFeedUnseenItemCount.set(res.data.userFeedUnseenCount);
        }
      });
  }

  private handleError(error: unknown, message: string): void {
    this.error.set(message);
    console.error(message, error);
    Sentry.captureException(error);
  }
}
